import { Injectable } from '@angular/core';
import { Selector, State, Action, StateContext } from '@ngxs/store';
import { AllocationViewFilterProfileModel } from '../../models';
import { AllocationViewFilterProfileItemService } from './allocation-view-filter-profile-item.service';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import {
  AllocationViewFilterProfileItemInitialState,
  AllocationViewFilterProfileItemCreate,
  AllocationViewFilterProfileItemLoad,
  AllocationViewFilterProfileItemSet,
  AllocationViewFilterProfileItemUpdate,
  AllocationViewFilterProfileItemDelete,
  AllocationViewFilterProfileItemGetSelected,
} from './allocation-view-filter-profile-item.actions';
import { catchError, map, Observable, of, take, tap } from 'rxjs';
import { CustomHttpErrorResponse } from '../../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AllocationViewFilterProfileItemStateModel {
  selectedProfile: AllocationViewFilterProfileModel;
  loaded: boolean;
}

const initialState: AllocationViewFilterProfileItemStateModel = {
  selectedProfile: null,
  loaded: false,
};

@State<AllocationViewFilterProfileItemStateModel>({
  name: 'allocationViewFilterProfileItem',
  defaults: initialState,
})
@Injectable()
export class AllocationViewFilterProfileItemState {
  constructor(
    private allocationViewFilterProfileItemService: AllocationViewFilterProfileItemService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: AllocationViewFilterProfileItemStateModel): AllocationViewFilterProfileItemStateModel {
    return state;
  }

  @Selector()
  static getSelectedProfile(state: AllocationViewFilterProfileItemStateModel): AllocationViewFilterProfileModel {
    return state.selectedProfile;
  }

  @Selector()
  static getLoaded(state: AllocationViewFilterProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Action(AllocationViewFilterProfileItemCreate)
  createFilterProfile(
    ctx: StateContext<AllocationViewFilterProfileItemStateModel>,
    { payload }: AllocationViewFilterProfileItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewFilterProfileItemService.createFilterProfile(payload.name, payload.filter).pipe(
      take(1),
      map((item: AllocationViewFilterProfileModel) => ctx.patchState({ selectedProfile: item, loaded: true })),
      tap(() => this.notificationService.success('monitoring.allocationView.filter.success.create')),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewFilterProfileItemUpdate)
  updateFilterProfile(
    ctx: StateContext<AllocationViewFilterProfileItemStateModel>,
    { payload }: AllocationViewFilterProfileItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewFilterProfileItemService.updateFilterProfile(payload).pipe(
      take(1),
      map((item: AllocationViewFilterProfileModel) => ctx.patchState({ selectedProfile: item, loaded: true })),
      tap(() => this.notificationService.success('monitoring.allocationView.filter.success.update')),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewFilterProfileItemDelete)
  deleteFilterProfile(
    ctx: StateContext<AllocationViewFilterProfileItemStateModel>,
    { payload, callback }: AllocationViewFilterProfileItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewFilterProfileItemService.deleteFilterProfile(payload).pipe(
      take(1),
      tap(() => {
        this.notificationService.success('monitoring.allocationView.filter.success.delete');

        if (callback) {
          callback();
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewFilterProfileItemLoad)
  loadFilterProfileById(
    ctx: StateContext<AllocationViewFilterProfileItemStateModel>,
    { id }: AllocationViewFilterProfileItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewFilterProfileItemService.getFilterProfileById(id).pipe(
      take(1),
      map((profile: AllocationViewFilterProfileModel) => {
        this.allocationViewFilterProfileItemService.setFilterProfile(profile);
        return ctx.patchState({ selectedProfile: profile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewFilterProfileItemSet)
  setFilterProfile(
    ctx: StateContext<AllocationViewFilterProfileItemStateModel>,
    { payload }: AllocationViewFilterProfileItemSet,
  ): void {
    this.allocationViewFilterProfileItemService.setFilterProfile(payload);
    ctx.patchState({ selectedProfile: payload, loaded: true });
  }

  @Action(AllocationViewFilterProfileItemGetSelected)
  getSelectedFilterProfile(ctx: StateContext<AllocationViewFilterProfileItemStateModel>): void {
    const selectedProfile = this.allocationViewFilterProfileItemService.getSelectedFilterProfile();
    ctx.patchState({ selectedProfile, loaded: true });
  }

  @Action(AllocationViewFilterProfileItemInitialState)
  setInitialState(ctx: StateContext<AllocationViewFilterProfileItemStateModel>): void {
    ctx.setState(initialState);
  }
}
